import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { assessmentsApi } from '../services/api';

const TrendingReport = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportingPdf, setExportingPdf] = useState(false);

  const baselineId = searchParams.get('baseline');
  const currentId = searchParams.get('current');

  useEffect(() => {
    if (baselineId && currentId) {
      loadComparison();
    } else {
      setError('Missing baseline or current assessment ID');
      setLoading(false);
    }
  }, [baselineId, currentId]);

  const loadComparison = async () => {
    try {
      setLoading(true);
      
      const response = await assessmentsApi.compare({
        baseline_id: parseInt(baselineId),
        current_id: parseInt(currentId)
      });
      
      setReport(response.data);
    } catch (error) {
      console.error('Error loading comparison:', error);
      setError(error.response?.data?.error || 'Failed to load trending report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      setExportingPdf(true);
      const token = localStorage.getItem('access_token');
      
      const response = await fetch('/api/pdf-reports/trending/pdf', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          baseline_id: parseInt(baselineId),
          current_id: parseInt(currentId)
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'Progress_Report.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }
      
      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#27ae60';
    if (score >= 60) return '#f39c12';
    if (score >= 40) return '#e67e22';
    return '#e74c3c';
  };

  const getChangeColor = (change) => {
    if (change > 0) return '#27ae60';
    if (change < 0) return '#e74c3c';
    return '#95a5a6';
  };

  const getChangeIcon = (change) => {
    if (change > 0) return '↑';
    if (change < 0) return '↓';
    return '→';
  };

  const getAnswerLabel = (answer) => {
    const labels = {
      'met': 'Met',
      'partially_met': 'Partially Met',
      'not_met': 'Not Met',
      'na': 'N/A'
    };
    return labels[answer] || answer;
  };

  const getAnswerColor = (answer) => {
    const colors = {
      'met': '#27ae60',
      'partially_met': '#f39c12',
      'not_met': '#e74c3c',
      'na': '#95a5a6'
    };
    return colors[answer] || '#95a5a6';
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading trending report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px' }}>
        <div style={{ color: '#e74c3c', marginBottom: '20px' }}>{error}</div>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!report) return null;

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>
          <button
            onClick={handleExportPdf}
            disabled={exportingPdf}
            style={{
              padding: '10px 20px',
              backgroundColor: exportingPdf ? '#95a5a6' : '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: exportingPdf ? 'not-allowed' : 'pointer',
              fontWeight: 'bold'
            }}
          >
            {exportingPdf ? '⏳ Generating PDF...' : '📄 Export PDF'}
          </button>
        </div>
        
        <h1 style={{ margin: '0 0 10px 0' }}>Trending Report</h1>
        <div style={{ color: '#666', fontSize: '16px' }}>
          <strong>{report.client_name}</strong> - {report.assessment_type_name}
        </div>
      </div>

      {/* Overall Score Comparison */}
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h2 style={{ marginTop: 0 }}>Overall Score Progress</h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '30px', alignItems: 'center' }}>
          {/* Baseline */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
              BASELINE
            </div>
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '15px' }}>
              {report.baseline.name}
            </div>
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '15px' }}>
              {formatDate(report.baseline.created_at)}
            </div>
            <div style={{
              fontSize: '48px',
              fontWeight: 'bold',
              color: getScoreColor(report.baseline.overall_score),
              marginBottom: '10px'
            }}>
              {Math.round(report.baseline.overall_score)}%
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              {report.baseline.met_count} Met, {report.baseline.partially_met_count} Partial, {report.baseline.not_met_count} Not Met
            </div>
          </div>

          {/* Change Arrow */}
          <div style={{ textAlign: 'center', minWidth: '100px' }}>
            <div style={{
              fontSize: '48px',
              color: getChangeColor(report.overall_change.score)
            }}>
              {getChangeIcon(report.overall_change.score)}
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: getChangeColor(report.overall_change.score)
            }}>
              {report.overall_change.score >= 0 ? '+' : ''}{Math.round(report.overall_change.score)}%
            </div>
          </div>

          {/* Current */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
              CURRENT
            </div>
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '15px' }}>
              {report.current.name}
            </div>
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '15px' }}>
              {formatDate(report.current.created_at)}
            </div>
            <div style={{
              fontSize: '48px',
              fontWeight: 'bold',
              color: getScoreColor(report.current.overall_score),
              marginBottom: '10px'
            }}>
              {Math.round(report.current.overall_score)}%
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              {report.current.met_count} Met, {report.current.partially_met_count} Partial, {report.current.not_met_count} Not Met
            </div>
          </div>
        </div>
      </div>

      {/* Category Progress - Top Level Only */}
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '10px' }}>Progress by Category</h2>
        <p style={{ color: '#666', fontSize: '14px', marginTop: 0, marginBottom: '20px' }}>
          Top-level categories only (e.g., Identify, Protect, Detect, Respond, Recover)
        </p>
        
        {report.section_comparison && report.section_comparison.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            No category data available
          </div>
        ) : (
          report.section_comparison && report.section_comparison.map(section => (
            <div key={section.section_id} style={{
              marginBottom: '15px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                  {section.section_name}
                </div>
                <div style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>Baseline</div>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: getScoreColor(section.baseline.weighted_score)
                    }}>
                      {Math.round(section.baseline.weighted_score)}%
                    </div>
                  </div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: getChangeColor(section.change.weighted_score)
                  }}>
                    {getChangeIcon(section.change.weighted_score)} {section.change.weighted_score >= 0 ? '+' : ''}{Math.round(section.change.weighted_score)}%
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>Current</div>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: getScoreColor(section.current.weighted_score)
                    }}>
                      {Math.round(section.current.weighted_score)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Changed Items */}
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px' }}>
          Detailed Changes ({report.changed_items ? report.changed_items.length : 0} items)
        </h2>

        {!report.changed_items || report.changed_items.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
            No changes found between these assessments
          </div>
        ) : (
          Object.entries(report.changed_by_section || {}).map(([sectionName, items]) => (
            <div key={sectionName} style={{ marginBottom: '30px' }}>
              <h3 style={{
                color: '#3498db',
                borderBottom: '2px solid #3498db',
                paddingBottom: '10px',
                marginBottom: '15px'
              }}>
                {sectionName} ({items.length} changes)
              </h3>

              {items.map(item => (
                <div key={item.question_id} style={{
                  marginBottom: '15px',
                  padding: '15px',
                  backgroundColor: item.change_type === 'improved' ? '#e8f5e9' : 
                                 item.change_type === 'declined' ? '#ffebee' : '#f5f5f5',
                  borderRadius: '4px',
                  borderLeft: `4px solid ${item.change_type === 'improved' ? '#27ae60' : 
                                          item.change_type === 'declined' ? '#e74c3c' : '#95a5a6'}`
                }}>
                  {item.subcategory_code && (
                    <div style={{
                      display: 'inline-block',
                      backgroundColor: '#3498db',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '3px',
                      fontSize: '12px',
                      marginBottom: '8px'
                    }}>
                      {item.subcategory_code}
                    </div>
                  )}
                  <div style={{ fontSize: '14px', marginBottom: '10px' }}>
                    {item.question_text}
                  </div>
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '12px', color: '#666' }}>Baseline:</span>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: getAnswerColor(item.baseline_answer),
                        color: 'white'
                      }}>
                        {getAnswerLabel(item.baseline_answer)}
                      </span>
                    </div>
                    <div style={{ fontSize: '20px', color: '#666' }}>→</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '12px', color: '#666' }}>Current:</span>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: getAnswerColor(item.current_answer),
                        color: 'white'
                      }}>
                        {getAnswerLabel(item.current_answer)}
                      </span>
                    </div>
                    <div style={{
                      marginLeft: 'auto',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      backgroundColor: item.change_type === 'improved' ? '#27ae60' : 
                                     item.change_type === 'declined' ? '#e74c3c' : '#95a5a6',
                      color: 'white'
                    }}>
                      {item.change_type === 'improved' ? '↑ IMPROVED' : 
                       item.change_type === 'declined' ? '↓ DECLINED' : '→ CHANGED'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TrendingReport;
